import { useState, useEffect, useRef } from 'react';
import {
  Button,
  ComboBox,
  DialogActions,
  Dialog,
  DialogHeader,
  DialogContent,
  IconButton,
  Tooltip,
} from '@airbus/components-react';
import { useColumnContextAPI } from '../../../context/GetAllColumnContext';
import { updateView } from '../../../services/updateViewManager';
import { useGridApiContext } from '@mui/x-data-grid-premium';
import isEqual from 'lodash.isequal';
import { createUseStyles } from 'react-jss';
import { t } from 'i18next';
import ViewsActions from './ViewsActions';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import styles from './ViewsActions.module.css';
import { useNotificationContext } from '../../../context/NotificationContext';

const useStyles = createUseStyles({
  comboBoxWrapper: {
    flex: '1 1 100px',
    minWidth: '150px',
    '@media (max-width: 600px)': {
      flex: '1 1 100%',
      minWidth: '0',
      maxWidth: '100%',
      paddingRight: 0,
    },
  },
  viewActionsWrapper: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',

    '@media (max-width: 600px)': {
      flex: '0 0 auto',
      display: 'flex',
      width: '100%',
      justifyContent: 'flex-end',
    },
  },
});

export default function ViewsList(props) {
  const classes = useStyles();
  const {
    setDefaultBtn,
    setAlertbar,
    isRowLoading,
    isUpdateing,
    setIsUpdateing,
    showViewActions = false,
    saveWidthsCheckBox,
    setSaveWidths = () => { },
  } = props;
  const apiRef = useGridApiContext();
  const {
    STANDARD_VIEW,
    selectedViewName,
    selectedView,
    accessDetails,
    initialViewColumns,
    viewData,
    setSelectedViewName,
    setSelectedView,
    setStateViewManagerData,
    tableDetails,
  } = useColumnContextAPI();
  const [views, setViews] = useState([]);
  const [newViewName, setNewViewName] = useState(null);
  const [isDisabled, setIsDisabled] = useState(false);
  const [value, setValue] = useState(null);
  const updateStates = useRef(false);

  const { processedNotificationId } = useNotificationContext();

  useEffect(() => {
    if (processedNotificationId) {
      viewData.map((v) => {
        if (v.id === Number(processedNotificationId)) {
          v.isActive = true;
        }
        return v;
      });

      if (viewData) {
        const listName = viewData.reduce((arr, item) => {
          if (item.viewName !== STANDARD_VIEW && item.isActive !== false) {
            arr.push({ label: item.viewName, value: item.viewName });
          }
          return arr;
        }, []);

        setViews(() => [{ label: STANDARD_VIEW, value: STANDARD_VIEW }, ...listName]);
      }
    }
  }, [processedNotificationId]);

  useEffect(() => {
    if (viewData) {
      const listName = viewData.reduce((arr, item) => {
        if (item.viewName !== STANDARD_VIEW && item.isActive !== false) {
          arr.push({ label: item.viewName, value: item.viewName });
        }
        return arr;
      }, []);

      setViews(() => [{ label: STANDARD_VIEW, value: STANDARD_VIEW }, ...listName]);
      setValue({ label: selectedViewName, value: selectedViewName });
    }
  }, [viewData]);

  useEffect(() => {
    if (viewData && selectedViewName) {
      const current = viewData.find((item) => item.defaultView);
      if (!updateStates) {
        const newViewDatas = viewData.filter((item) => item.defaultView);
        apiRef?.current?.restoreState(newViewDatas[0].gridFilter);
        updateStates.current = true;
      }
      if (current) {
        setIsDisabled(() => selectedViewName === current.viewName);
      } else {
        setIsDisabled(() => selectedViewName === STANDARD_VIEW);
      }
    }
  }, [selectedViewName, viewData]);

  useEffect(() => {
    if (selectedViewName && selectedViewName != value?.value) {
      setValue(selectedViewName);
    }
  }, [selectedViewName]);

  useEffect(() => {
    if (!selectedView) return;

    if (selectedView.viewName === 'Standard View') {
      setSaveWidths(false);
      return;
    }

    if (selectedView.viewColumns && Array.isArray(selectedView.viewColumns)) {
      // Extract numeric widths from strings like 'width:150px'
      const hasCustomWidth = selectedView.viewColumns.some((col: string) => {
        const match = col.match(/width:(\d+)px/);
        return match && parseInt(match[1], 10) > 150;
      });

      if (hasCustomWidth) {
        setSaveWidths(true);
        return;
      }
      else {
        setSaveWidths(false);
      }
    }

    if (selectedView.id) {
      setSaveWidths(!!selectedView.saveWidths);
    } else {
      setSaveWidths(false);
    }
  }, [selectedView]);

  const handleUpdate = async () => {
    const { viewName, id } = selectedView;
    setIsUpdateing(true);
    updateView({ viewName, defaultView: true }, id).then(() => {
      setIsUpdateing(false);
      const crntDefaultList = viewData.filter((item) => item.defaultView);
      for (const currentItem of crntDefaultList) {
        const { viewName, id } = currentItem;
        updateView({ viewName, defaultView: false }, id);
      }

      const updateViewData = viewData.map((item) => {
        return { ...item, defaultView: selectedViewName === item.viewName };
      });
      setIsDisabled(true);
      setStateViewManagerData(() => [...updateViewData]);
      setSelectedView({ ...selectedView, defaultView: true });
      setAlertbar({ children: t('view.default_view_changed'), severity: 'success' });
    });
  };
  const changeViewHandler = (newValue) => {
    const exportedStates = selectedView.gridFilter;
    const appliedFilters = exportedStates?.filter.filterModel.items || [];
    const currentGlobalField = exportedStates?.filter.filterModel.quickFilterValues || [];
    const appliedSort = exportedStates?.sorting.sortModel || [];
    const appliedPage =
      exportedStates?.pagination.paginationModel.pageSize || accessDetails.noOfRowsPerPage;
    if (typeof newValue !== 'undefined' && newValue !== null) {
      setValue(newValue);
      const newViewData = viewData?.find((item) => item.viewName === newValue?.value);
      if (!newViewData) {
        return;
      }
      const isGridStateChanged = IsAnyChangesInCurrentView({
        apiRef,
        appliedFilters,
        currentGlobalField,
        appliedSort,
        appliedPage,
        initialViewColumns,
        tableDetails,
      });

      if (isGridStateChanged) {
        setNewViewName(newValue.value);
        return;
      }
      setSelectedViewName(newValue.value);
    }
  };
  const BlurHandler = () => {
    if (!value) {
      setValue({ label: selectedViewName, value: selectedViewName });
    }
  };
  return (
    <>
      <div className={`${styles.viewlist} viewlist`}>
        <div className={classes.comboBoxWrapper}>
          <ComboBox
            className='listbox'
            value={value}
            onChange={(obj) => changeViewHandler(obj)}
            id='combo-box-demo'
            options={views}
            size='medium'
            keepInputOnSelect
            hideClear
            onBlur={BlurHandler}
            disabled={isRowLoading}
          />
        </div>
        {showViewActions && (
          <>
            <div className={classes.viewActionsWrapper}>
              <div className={styles.iconMargin}>
                <div className={`${styles.iconRow} iconRow`}>
                  {setDefaultBtn && (
                    <Tooltip label={t('view.set_default_view')}>
                      <IconButton
                        onClick={handleUpdate}
                        disabled={isDisabled || isUpdateing}
                        variant='ghost'
                        className={isDisabled ? '' : styles.iconButtonStyle}
                      >
                        <TaskAltRoundedIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  )}

                  <ViewsActions
                    apiRef={apiRef}
                    setAlertbar={setAlertbar}
                    isUpdateing={isUpdateing}
                    setIsUpdateing={setIsUpdateing}
                    saveWidthsCheckBox={saveWidthsCheckBox}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <ConfirmChangesNotSavedBox
        {...{
          newViewName,
          setNewViewName,
          setValue,
          selectedViewName,
          apiRef,
          selectedView,
          viewData,
          setSelectedView,
        }}
      />
    </>
  );
}
function deepEqual(currentObj, prevObj) {
  const currentKeys = Object.keys(currentObj);
  const prevKeys = Object.keys(prevObj);
  if (currentKeys.length !== prevKeys.length) {
    return false;
  }
  return currentKeys.every((key) => currentObj[key] === prevObj[key]);
}
function checkEqualObjectArray(currentArray, prevArray) {
  if (currentArray.length !== prevArray.length) {
    return false;
  }
  return currentArray?.every((item) => prevArray.some((item2) => deepEqual(item, item2)));
}
function checkArrayEqual(firstArr, secondArr) {
  if (firstArr.length !== secondArr.length) {
    return false;
  }
  for (let i = 0; i < firstArr.length; i++) {
    if (firstArr[i] !== secondArr[i]) {
      return false;
    }
  }
  return true;
}
export function IsAnyChangesInCurrentView(props) {
  const {
    apiRef,
    initialViewColumns,
    tableDetails,
    appliedFilters,
    appliedSort,
    appliedPage,
    currentGlobalField,
  } = props;
  const gridFilter = apiRef.current.exportState();
  if (!gridFilter) {
    return true;
  }
  const appliedSortWithoutId = appliedSort.filter((item) => item.field !== 'id');
  const checkFilter = checkEqualObjectArray(appliedFilters, gridFilter?.filter.filterModel.items);
  const checkSort = checkEqualObjectArray(appliedSortWithoutId, gridFilter?.sorting.sortModel);
  const checkPage = gridFilter?.pagination.paginationModel.pageSize;
  const globalSearch = gridFilter?.filter.filterModel.quickFilterValues;
  const checkGlobalItem = checkArrayEqual(currentGlobalField, globalSearch);
  if (!checkFilter || !checkSort || appliedPage !== checkPage || !checkGlobalItem) {
    return true;
  }
  const gridOrderedFields = gridFilter?.columns?.orderedFields;
  const uniqueId = tableDetails?.mbdDataGrid?.dataGridUniqueIdentifier;
  const hasUniqueIdInInitial = initialViewColumns?.includes(uniqueId);
  const apiOrderedFields = gridOrderedFields?.reduce((resp, item) => {
    if (
      item != 'undoDelete' &&
      item != 'id' &&
      !(item === uniqueId && !hasUniqueIdInInitial)
    ) {
      resp.push(item);
    }
    return resp;
  }, []);

  const viewColumnsCheck = isEqual(apiOrderedFields, initialViewColumns);
  if (viewColumnsCheck) {
    return false;
  }
  return true;
}
export function ConfirmChangesNotSavedBox(props) {
  const {
    newViewName,
    setNewViewName,
    setValue,
    selectedViewName,
    apiRef,
    selectedView,
    setSelectedView,
    viewData,
  } = props;
  const { setSelectedViewName } = useColumnContextAPI();
  const okHandler = () => {
    if (newViewName === selectedViewName) {
      if (selectedView.gridFilter) {
        apiRef.current.restoreState(selectedView.gridFilter);
      }
      const newViewDatas = viewData.filter((item) => item.viewName === newViewName);
      setSelectedView((prestate) => ({ ...prestate, viewColumns: newViewDatas[0].viewColumns }));
    }
    setSelectedViewName(newViewName);
    setNewViewName(null);
  };

  const cancelHandler = () => {
    setNewViewName(null);
    setValue({ label: selectedViewName, value: selectedViewName });
  };

  return <ChangesWarningDialog {...{ isOpen: !!newViewName, cancelHandler, okHandler }} />;
}

export const ChangesWarningDialog = (props) => {
  const { isOpen, cancelHandler, okHandler } = props;
  return (
    <Dialog
      open={isOpen}
      onClose={cancelHandler}
      aria-describedby='alert-dialog-slide-description'
      style={{ zIndex: 1402 }}
    >
      <DialogHeader>{t('confirm')}</DialogHeader>
      <DialogContent>{t('view.changes_not_saved')}</DialogContent>
      <DialogActions>
        <Button onClick={cancelHandler} variant='secondary'>
          {t('cancel')}
        </Button>
        <Button onClick={okHandler} variant='primary'>
          {t('ok')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
